class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');
      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    this.addEventListener('click', () => {
      const wrapper = this.closest('.card-wrapper');
      const drawer = wrapper.querySelector('quick-view-drawer');
      if (drawer) {
        drawer.querySelector('summary').click();

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      }
      else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
    this._delegatedClickBound = false;
    this.onDelegatedClick = this.onDelegatedClick.bind(this);
  }

  connectedCallback() {
    if (!this._delegatedClickBound) {
      // Delegated handler survives innerHTML swaps
      this.addEventListener('click', this.onDelegatedClick);
      this._delegatedClickBound = true;
    }
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  onDelegatedClick(event) {
    const link = event.target.closest?.('.pl-swatches__link');
    if (!link || !this.contains(link)) return;

    // Only handle swatches inside quick-view injected content
    const drawerContent = this.querySelector('.quick-view__content');
    if (!drawerContent || !drawerContent.contains(link)) return;

    // Ignore disabled / active / JS void links
    if (
      link.classList.contains('pl-swatches__link--disabled') ||
      link.classList.contains('pl-swatches__link--active')
    ) {
      event.preventDefault();
      return;
    }

    const href = link.getAttribute('href') || '';
    if (!href || href.startsWith('javascript:') || href === '#') return;

    event.preventDefault();
    event.stopPropagation();

    const preserved = this.getPreservedOptions(drawerContent);
    const nextProductUrl = href;
    this.loadIntoDrawer(nextProductUrl, preserved);
  }

  getPreservedOptions(drawerContent) {
    const COLOR_RE = /^(color|colour|цве(?:т|та)|farbe|couleur)$/i;
    const SIZE_HINT_RE = /(size|размер)/i;

    const options = new Map();

    // Dropdowns: name="options[Size]"
    drawerContent.querySelectorAll('variant-selects select[name^="options["]').forEach(select => {
      const match = select.name.match(/^options\[(.*)\]$/);
      const optionName = match?.[1];
      if (!optionName) return;
      options.set(optionName, select.value);
    });

    // Buttons: name like "Size-2"
    drawerContent.querySelectorAll('variant-selects input[type="radio"]:checked').forEach(input => {
      const rawName = input.getAttribute('name') || '';
      const optionName = rawName.replace(/-\d+$/, '');
      if (!optionName) return;
      options.set(optionName, input.value);
    });

    // Prefer keeping only "Size" (or similar). If we can't detect it, keep all non-color options.
    const preserved = new Map();
    for (const [name, value] of options.entries()) {
      if (COLOR_RE.test(name)) continue;
      if (SIZE_HINT_RE.test(name)) preserved.set(name, value);
    }

    if (preserved.size > 0) return preserved;

    // Fallback: preserve everything except color
    for (const [name, value] of options.entries()) {
      if (COLOR_RE.test(name)) continue;
      preserved.set(name, value);
    }
    return preserved;
  }

  restoreOptions(drawerContent, preserved) {
    if (!preserved || preserved.size === 0) return;

    const selects = Array.from(drawerContent.querySelectorAll('variant-selects select[name^="options["]'));
    const radios = Array.from(drawerContent.querySelectorAll('variant-selects input[type="radio"]'));

    const setAndNotify = el => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    };

    for (const [optionName, value] of preserved.entries()) {
      // Try selects first
      const select = selects.find(s => s.name === `options[${optionName}]`);
      if (select) {
        const hasValue = Array.from(select.options).some(o => o.value === value);
        if (hasValue) {
          select.value = value;
          setAndNotify(select);
          continue;
        }
      }

      // Then radio groups: match by parsed option name
      const group = radios.filter(r => (r.name || '').replace(/-\d+$/, '') === optionName);
      const target = group.find(r => r.value === value && !r.disabled);
      if (target) {
        target.checked = true;
        setAndNotify(target);
      }
    }
  }

  buildModalUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('view', 'modal');
    return `${url.pathname}${url.search}`;
  }

  applyQuickAddDatasetOverrides(drawerContent) {
    if (!drawerContent || this.dataset.hasQuickAddSizeInfo !== 'true') return;

    const labelUnderGallery = this.dataset.quickAddLabelUnderGallery || '';
    const labelUnderGalleryHtml = this.dataset.quickAddLabelUnderGalleryHtml || '';
    const labelNearSize = this.dataset.quickAddLabelNearSize || '';
    const labelBeforeSizes = this.dataset.quickAddLabelBeforeSizes || '';
    const labelBeforeSizesHtml = this.dataset.quickAddLabelBeforeSizesHtml || '';

    const waistBySize = {
      xs: this.dataset.quickAddXsWaist || '',
      s: this.dataset.quickAddSWaist || '',
      m: this.dataset.quickAddMWaist || '',
      l: this.dataset.quickAddLWaist || '',
      xl: this.dataset.quickAddXlWaist || ''
    };

    drawerContent.querySelectorAll('.quick_add_size_info__label-under-gallery').forEach((node) => {
      if (labelUnderGalleryHtml !== '') {
        node.innerHTML = labelUnderGalleryHtml;
      } else if (labelUnderGallery !== '') {
        node.textContent = labelUnderGallery;
      }
    });

    drawerContent.querySelectorAll('.variant-size-near-label').forEach((node) => {
      if (labelNearSize !== '') node.textContent = labelNearSize;
    });

    drawerContent.querySelectorAll('.quick-view__label-before-sizes').forEach((node) => {
      if (labelBeforeSizesHtml !== '') {
        node.innerHTML = labelBeforeSizesHtml;
      } else if (labelBeforeSizes !== '') {
        node.textContent = labelBeforeSizes;
      }
    });

    drawerContent.querySelectorAll('.product-form__input--pill label[for]').forEach((label) => {
      const inputId = label.getAttribute('for');
      if (!inputId) return;
      const input = drawerContent.querySelector(`#${CSS.escape(inputId)}`);
      const sizeValue = (input?.value || '').trim().toLowerCase();
      if (!sizeValue) return;

      const waistValue = waistBySize[sizeValue];
      let waistNode = label.querySelector('.variant-size-waist');

      if (waistValue) {
        if (!waistNode) {
          waistNode = document.createElement('span');
          waistNode.className = 'quick_add_size_info variant-size-waist';
          label.appendChild(waistNode);
        }
        waistNode.textContent = waistValue;
      } else if (waistNode) {
        waistNode.remove();
      }
    });
  }

  handleIntersection(entries, _observer) {
    if (!entries[0].isIntersecting) return;

    const selector = '.quick-view__content';
    const drawerContent = this.querySelector(selector);
    const productUrl = this.dataset.productUrl;
    const sectionUrl = this.buildModalUrl(productUrl);

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector(selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);
          this.applyQuickAddDatasetOverrides(drawerContent);
          this.applySwatchesVisibility(drawerContent);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        }, 200);

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.dataset.productUrl
            }
          }));
        }, 500);
      })
      .catch(e => {
        console.error(e);
      });
  }

  loadIntoDrawer(productUrl, preserved) {
    const selector = '.quick-view__content';
    const drawerContent = this.querySelector(selector);
    if (!drawerContent) return;

    drawerContent.classList.remove('hide-cover');

    const normalizedUrl = productUrl;
    const sectionUrl = this.buildModalUrl(normalizedUrl);

    // Keep dataset in sync for future events
    this.dataset.productUrl = normalizedUrl;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector(selector);
          this.setInnerHTML(drawerContent, productElement?.innerHTML || '');
          this.applyQuickAddDatasetOverrides(drawerContent);
          this.applySwatchesVisibility(drawerContent);

          // Re-apply size (or other non-color options) after swapping product
          this.restoreOptions(drawerContent, preserved);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        }, 50);

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: { productUrl: normalizedUrl }
          }));
        }, 250);
      })
      .catch(e => console.error(e));
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  applySwatchesVisibility(drawerContent) {
    if (this.dataset.enableColorSwatches === 'false') {
      drawerContent.querySelectorAll('.pl-swatches').forEach(el => el.remove());
    }
  }
}
customElements.define('quick-view', QuickView);
